<template>
  <RevCard class="p-16" data-test="review-display">
    <div class="flex flex-col items-start">
      <div
        class="flex w-full flex-col items-start justify-between gap-12 md:flex-row"
      >
        <div class="w-full md:w-auto">
          <div class="flex items-center justify-between gap-8 md:justify-start">
            <h3 class="heading-3">{{ userName }}</h3>

            <RevTag :label="i18n(translations.verified)" variant="info" />
          </div>

          <p v-if="reviewInfotext" class="text-static-default-low body-2">
            {{ reviewInfotext }}
            <Flag
              v-if="countryCode"
              aria-hidden="true"
              class="mr-4 inline-block align-baseline"
              :country-code="countryCode as MarketCountryCode"
            />
          </p>
        </div>

        <RevRating :score="averageRate" />
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { LocaleCode } from '@backmarket/creative-pages/constants/locales'
import type { Link } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { SalesOrderItem } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'

import translations from './ReviewDisplay.translations'

export type UserComment = {
  id: string
  comment: string
  averageRate: number
  translatedComment?: string
  customer: { firstName?: string; lastName?: string }
  seller?: { companyName: string; companySlug: string; id: string }
  countryCode: string
  reviewDate: string
  purchaseDate: string
  product: {
    title: string
    link: Link | string
    id: string
    imageUrl: string
    locale: LocaleCode
    model: string
    purchasedAt: string
  }
  languageCode: string
  salesOrderItem: SalesOrderItem
}

export type UserCommentProps = UserComment & {
  trackingData?: {
    onClick: Record<string, unknown>
    onImpression: Record<string, unknown>
  }
  trackReviewImpression?: boolean
  displaySeller?: boolean
}

const i18n = useI18n()

const props = withDefaults(defineProps<UserCommentProps>(), {
  trackingData: () => ({
    onClick: {},
    onImpression: {},
  }),
  translatedComment: undefined,
  trackReviewImpression: false,
  seller: undefined,
  displaySeller: false,
})

const userName = computed(() => {
  if (props.customer.firstName && props.customer.lastName) {
    return `${props.customer.firstName} ${props.customer.lastName}`
  }

  return (
    props.customer.firstName ??
    props.customer.lastName ??
    i18n(translations.anonymousUser)
  )
})

const reviewInfotext = computed(() =>
  props.purchaseDate && props.reviewDate && props.countryCode
    ? i18n(translations.reviewInfo, {
        reviewDate: i18n.date(new Date(props.reviewDate), {
          dateStyle: 'short',
        }),
        purchaseDate: i18n.date(new Date(props.purchaseDate), {
          dateStyle: 'short',
        }),
        country: i18n.country(props.countryCode),
      })
    : '',
)
</script>
